import type { AxiosInstance } from 'axios';
/**
 * Agent dashboard data service.
 *
 * The dashboard composes its widgets from the existing REST endpoints
 * — there is no aggregator endpoint on the backend. Each widget has
 * its own fetch fn so the SSE event hook can refetch one widget
 * without disturbing the others.
 *
 * Real-time updates arrive over SSE (see `useAgentEventStream`);
 * payloads carry a `trigger_widgets` hint that maps to the keys in
 * `WidgetKey` below — the dashboard then calls the matching fetcher.
 */
import authService from './auth.service';
import { readerLocale } from '../i18n/language';

export type WidgetKey =
  | 'kpis'
  | 'upcoming-appointments'
  | 'pending-cases'
  | 'overdue-followups'
  | 'pending-approvals'
  | 'alerts';

// ─── Wire-format types ─────────────────────────────────────────────
export interface AppointmentParty {
  id: string;
  first_name: string;
  last_name: string;
  email?: string | null;
}

export interface AppointmentItem {
  id: string;
  agent: AppointmentParty | null;
  original_agent: AppointmentParty;
  member: AppointmentParty;
  case_id: string | null;
  starts_at: string;
  ends_at: string;
  status: string;
  purpose: string | null;
  is_orphaned: boolean;
  needs_my_action: boolean;
}

export interface CaseItem {
  case_id: string;
  workflow_slug: string;
  current_state_slug: string;
  status: string;
  user_id: string;
  client_first_name: string;
  client_last_name: string;
  client_email: string;
  assigned_to: string | null;
  agent_first_name: string | null;
  agent_last_name: string | null;
  created_at: string;
  last_activity_at: string;
  expires_at: string | null;
  needs_my_action: boolean;
  assignee_unavailable: boolean;
}

export interface DepositItem {
  id: string;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  product_name: string;
  amount: number;
  currency_code: string;
  reference_number: string | null;
  status: string;
  proposed_by: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
}

export interface AlertItem {
  /** Stable id so React keys are predictable across refetches. */
  id: string;
  severity: 'info' | 'soft' | 'firm';
  title: string;
  detail: string;
  /** Optional in-app target. */
  link?: string;
}

// ─── Composed KPI snapshot ─────────────────────────────────────────
export interface DashboardKPIs {
  appointmentsToday: number;
  appointmentsThisWeek: number;
  casesPending: number;
  overdueCount: number;
  approvalsWaiting: number;
  /** Count of KYC cases the agent submitted to a supervisor that are
   *  still awaiting a decision. Derived from the existing pending-cases
   *  list (assigned_to stays on the agent through SUBMIT_FOR_DECISION /
   *  PROPOSE_REJECTION; the case appears with the corresponding state
   *  slug). Refreshes via the same `pending-cases` / `kpis` SSE widget
   *  refetchers that fire on `case.transitioned`. */
  kycHandoffsWaiting: number;
  alertsCount: number;
}

/** States the agent has handed off to a supervisor — the case is no
 *  longer editable by them but stays on their roster so it can be
 *  counted on the dashboard. Single source of truth so the KPI and
 *  any future "awaiting decision" filter agree. */
export const KYC_HANDOFF_STATES: readonly string[] = [
  'pending_decision',
  'pending_rejection',
];

// ─── Helpers ───────────────────────────────────────────────────────
function todayBoundsISO(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { start: start.toISOString(), end: end.toISOString() };
}

function weekBoundsISO(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return { start: start.toISOString(), end: end.toISOString() };
}

/** Cases inside this window count as "alerting" — SLA almost out. */
const ALERT_WINDOW_DAYS = 14;

class AgentDashboardService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** Upcoming appointments — today through +7 days, only `scheduled`. */
  async fetchUpcomingAppointments(): Promise<AppointmentItem[]> {
    const { start } = todayBoundsISO();
    const { end } = weekBoundsISO();
    const res = await this.api.get<{ items: AppointmentItem[] }>(
      '/api/v1/scheduling/appointments',
      {
        params: {
          from: start,
          to: end,
          assigned_to: 'me',
          status: 'scheduled',
          size: 50,
        },
      },
    );
    return res.data.items;
  }

  /** Cases assigned to the caller and still in progress. */
  async fetchPendingCases(): Promise<CaseItem[]> {
    const res = await this.api.get<{ items: CaseItem[] }>(
      '/api/v1/workflow/cases',
      {
        params: {
          assigned_to: 'me',
          // status omitted → backend defaults to non-terminal statuses
          size: 50,
        },
      },
    );
    return res.data.items;
  }

  /** Subset of pending cases past their SLA deadline. Computed
   *  client-side from the same list to avoid a second round-trip. */
  filterOverdue(cases: CaseItem[]): CaseItem[] {
    const now = Date.now();
    return cases.filter(
      (c) => c.expires_at !== null && Date.parse(c.expires_at) < now,
    );
  }

  /** Cases approaching SLA but not yet past it. Feeds the alerts widget. */
  filterApproachingExpiry(cases: CaseItem[]): CaseItem[] {
    const now = Date.now();
    const horizon = now + ALERT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return cases.filter((c) => {
      if (c.expires_at === null) return false;
      const exp = Date.parse(c.expires_at);
      return exp >= now && exp <= horizon;
    });
  }

  /** Deposits the agent proposed that are still awaiting a manager
   *  decision. Backend RBAC scopes "list deposits" to the caller for
   *  agents, so this returns only mine. */
  async fetchPendingApprovals(): Promise<DepositItem[]> {
    const res = await this.api.get<{ items: DepositItem[] }>(
      '/api/v1/deposits',
      {
        params: {
          status: 'pending_review',
          size: 50,
        },
      },
    );
    return res.data.items;
  }

  /** Build the alerts list from pending cases. Today's sources:
   *    · assignee_unavailable (manager hasn't reassigned during time-off)
   *    · cases inside the ALERT_WINDOW_DAYS expiry window
   *  Future sources (KYC expiring among the roster, retention dates)
   *  will plug in here once their endpoints exist. */
  buildAlerts(cases: CaseItem[]): AlertItem[] {
    const alerts: AlertItem[] = [];

    for (const c of cases) {
      if (c.assignee_unavailable) {
        alerts.push({
          id: `unavail:${c.case_id}`,
          severity: 'soft',
          title: 'Assignee on time-off',
          detail: `${c.client_first_name} ${c.client_last_name} — case is yours but you're flagged as unavailable.`,
          link: `/agent/cases/${c.case_id}`,
        });
      }
    }

    const approaching = this.filterApproachingExpiry(cases);
    for (const c of approaching) {
      alerts.push({
        id: `expiring:${c.case_id}`,
        severity: 'soft',
        title: 'SLA expiring soon',
        detail: `${c.client_first_name} ${c.client_last_name} — ${c.workflow_slug} expires ${new Date(c.expires_at!).toLocaleDateString(readerLocale())}.`,
        link: `/agent/cases/${c.case_id}`,
      });
    }

    return alerts;
  }

  /** Computes the KPI strip from the already-fetched lists, no
   *  extra requests. Centralized so the dashboard component and any
   *  future widgets stay aligned on the counting rules. */
  buildKPIs(args: {
    appointments: AppointmentItem[];
    cases: CaseItem[];
    overdue: CaseItem[];
    approvals: DepositItem[];
    alerts: AlertItem[];
  }): DashboardKPIs {
    const { start, end } = todayBoundsISO();
    const startMs = Date.parse(start);
    const endMs = Date.parse(end);
    const appointmentsToday = args.appointments.filter((a) => {
      const t = Date.parse(a.starts_at);
      return t >= startMs && t < endMs;
    }).length;

    const kycHandoffsWaiting = args.cases.filter(
      (c) => KYC_HANDOFF_STATES.includes(c.current_state_slug),
    ).length;

    return {
      appointmentsToday,
      appointmentsThisWeek: args.appointments.length,
      casesPending: args.cases.length,
      overdueCount: args.overdue.length,
      approvalsWaiting: args.approvals.length,
      kycHandoffsWaiting,
      alertsCount: args.alerts.length,
    };
  }
}

export default new AgentDashboardService();
